import React from 'react';
import eventBus, { EVENT_NAMES } from '../../utils/event_bus';
import MapView from '../components/map/map_view';
import DriverListView from './driver_list_view';
import { Box, Paper, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';

interface InitialMapQuery {
  lat?: number;
  lon?: number;
  address: string;
  distance: number | null;
}

const parseQueryNumber = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const getInitialMapQuery = (search: string): InitialMapQuery => {
  const queryParams = new URLSearchParams(search);
  const lat = parseQueryNumber(queryParams.get('lat'));
  const lon = parseQueryNumber(queryParams.get('lon'));
  const distanceParam = parseQueryNumber(queryParams.get('distance'));
  const address = queryParams.get('address') || '';
  const hasLocation = lat !== undefined && lon !== undefined;

  return {
    lat,
    lon,
    address,
    distance: hasLocation ? distanceParam ?? 50 : null,
  };
};

/**
 * 司机地图页面
 * 显示地图和悬浮在上面的司机列表
 */
const MapDrivers: React.FC = () => {
  const location = useLocation();
  const initialQuery = React.useMemo(
    () => getInitialMapQuery(location.search),
    [location.search]
  );

  const mapCenter = React.useMemo<[number, number]>(
    () =>
      initialQuery.lat !== undefined && initialQuery.lon !== undefined
        ? [initialQuery.lat, initialQuery.lon]
        : [39.9042, 116.4074],
    [initialQuery.lat, initialQuery.lon]
  );

  const handleMapReady = React.useCallback(() => {
    if (initialQuery.lat === undefined || initialQuery.lon === undefined) {
      return;
    }

    eventBus.publish(
      EVENT_NAMES.MAP_LOCATION_CHANGE,
      initialQuery.lat,
      initialQuery.lon,
      initialQuery.address
    );

    if (initialQuery.distance !== null) {
      eventBus.publish(EVENT_NAMES.DISTANCE_CHANGE, initialQuery.distance);
    }
  }, [initialQuery]);

  return (
    <Box sx={{ position: 'relative', height: 'calc(100vh - 40px)', width: '100%' }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
        <MapView
          center={mapCenter}
          zoom={10}
          height="100%"
          width="100%"
          onMapReady={handleMapReady}
        />
      </Box>

      <Paper
        elevation={6}
        sx={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          height: 'calc(100vh - 80px)',
          width: '35%',
          maxWidth: '100%',
          borderRadius: '8px',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          overflow: 'auto',
          zIndex: 1000,
        }}
      >
        <DriverListSidebar />
      </Paper>
    </Box>
  );
};

const DriverListSidebar: React.FC = () => {
  return (
    <Box sx={{ padding: '2px' }}>
      <Typography
        sx={{
          color: 'white',
          textAlign: 'center',
          marginBottom: '10px',
          padding: '5px 0',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          fontWeight: 'bold',
        }}
      >
        司机列表
      </Typography>
      <DriverListView />
    </Box>
  );
};

export default MapDrivers;
